import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, BellRing, Copy, IndianRupee, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errText } from "@/lib/api";

const rupees = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;
const monthName = (m) =>
  new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

export default function Collections() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const res = await api.get("/stay/collections");
      setData(res.data);
    } catch (err) {
      toast.error(errText(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => {
    const rows = data?.rows || [];
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) =>
      [r.tenant_name, r.stay_name, r.bed_label, r.room_name, r.tenant_phone]
        .join(" ")
        .toLowerCase()
        .includes(needle)
    );
  }, [data, q]);

  const remind = async (row) => {
    setBusy(row.bed_id);
    try {
      const { data: res } = await api.post(`/stay/beds/${row.bed_id}/remind`);
      toast.success(res.status === "sent" ? `Reminder emailed to ${row.tenant_name}` : "Already reminded today");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  const remindAll = async () => {
    setBusy("all");
    try {
      const { data: res } = await api.post("/stay/collections/remind-all");
      toast.success(res.reminders_sent ? `${res.reminders_sent} reminder(s) sent` : "Everyone was reminded recently");
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  const settle = async (row, month) => {
    setBusy(row.bed_id + month);
    try {
      await api.post(`/stay/beds/${row.bed_id}/settle`, { month });
      toast.success(`${monthName(month)} marked received — receipt emailed`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy("");
    }
  };

  const copyLink = (row) => {
    navigator.clipboard.writeText(`${window.location.origin}/pay/${row.pay_token}`);
    toast.success("Payment link copied");
  };

  const waPhone = (raw) => {
    const digits = (raw || "").replace(/\D/g, "");
    if (digits.length === 10) return `91${digits}`;
    if (digits.length === 12 && digits.startsWith("91")) return digits;
    if (digits.length > 12 && digits.length <= 15) return digits;
    return "";
  };

  const whatsapp = (row) => {
    const to = waPhone(row.tenant_phone);
    if (!to) return toast.error("Add a valid 10-digit mobile number for this tenant first");
    const link = `${window.location.origin}/pay/${row.pay_token}`;
    const months = row.months.map(monthName).join(", ");
    const text =
      `Hi ${row.tenant_name}, this is a reminder for your rent at ${row.stay_name} ` +
      `(${row.bed_label}). Pending: ${months} — total ₹${Number(row.outstanding).toLocaleString("en-IN")}. ` +
      `You can see the amount and my UPI / bank details here: ${link}`;
    window.open(`https://wa.me/${to}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  if (!data)
    return (
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );

  const stats = [
    ["Outstanding", rupees(data.totals.outstanding)],
    ["Tenants pending", data.totals.tenants],
    ["Months pending", data.totals.months],
    ["Collected this month", rupees(data.totals.collected_this_month)],
  ];

  return (
    <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-10" data-testid="collections-page">
      <Link to="/stays" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-muted-foreground transition-colors duration-200 hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> All PGs
      </Link>
      <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl">Collections board</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            Who owes what across all your PGs — most overdue first
          </p>
        </div>
        <Button
          onClick={remindAll}
          disabled={busy === "all" || rows.length === 0}
          className="h-12 rounded-lg font-bold hover:brightness-95"
          data-testid="collections-remind-all-btn"
        >
          <Send className="mr-2 h-4 w-4" /> {busy === "all" ? "Sending…" : "Remind everyone pending"}
        </Button>
      </div>

      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map(([label, value]) => (
          <div key={label} className="rounded-2xl border border-border p-5" data-testid={`collections-stat-${label.toLowerCase().replace(/ /g, "-")}`}>
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
            <p className="mt-2 font-display text-2xl">{value}</p>
          </div>
        ))}
      </div>

      <Input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search tenant, PG, room or bed"
        className="mt-7 h-12 max-w-sm rounded-lg"
        data-testid="collections-search-input"
      />

      <div className="mt-5 rounded-2xl border border-border overflow-x-auto">
        <Table className="min-w-[920px]" data-testid="collections-table">
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>PG · room · bed</TableHead>
              <TableHead>Months pending · tap to mark received</TableHead>
              <TableHead>Oldest due</TableHead>
              <TableHead>Outstanding</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-sm text-muted-foreground" data-testid="collections-empty">
                  Nothing pending — every tenant is up to date.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.bed_id} data-testid={`collections-row-${r.bed_id}`}>
                  <TableCell>
                    <p className="text-sm font-bold">{r.tenant_name}</p>
                    <p className="text-[12px] text-muted-foreground">{r.tenant_phone || r.tenant_email || "—"}</p>
                  </TableCell>
                  <TableCell className="text-[13px]">
                    <Link to={`/stays/${r.stay_id}`} className="font-semibold underline-offset-4 hover:underline">
                      {r.stay_name}
                    </Link>
                    <p className="text-[12px] text-muted-foreground">
                      {r.room_name} · {r.bed_label} · due day {r.due_day}
                    </p>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1.5">
                      {r.months.map((m) => {
                        const claimed = (r.claimed_months || []).includes(m);
                        return (
                          <button
                            key={m}
                            onClick={() => settle(r, m)}
                            disabled={busy === r.bed_id + m}
                            title={claimed ? "Tenant says this month is paid — confirm it" : "Mark this month as received"}
                            className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors duration-200 ${
                              claimed
                                ? "bg-amber-500/15 text-amber-700 hover:bg-amber-500/25"
                                : "bg-muted hover:bg-foreground hover:text-background"
                            }`}
                            data-testid={`collections-settle-${r.bed_id}-${m}`}
                          >
                            {monthName(m)}
                            {claimed ? " · says paid" : ""}
                          </button>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="text-[13px] font-semibold">{monthName(r.oldest_month)}</TableCell>
                  <TableCell className="flex items-center gap-0.5 text-sm font-bold">
                    <IndianRupee className="h-3.5 w-3.5" />
                    {Number(r.outstanding).toLocaleString("en-IN")}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-lg"
                        disabled={busy === r.bed_id || !r.tenant_email || r.reminders_off}
                        onClick={() => remind(r)}
                        title={r.reminders_off ? "Tenant opted out of reminders" : "Email a reminder now"}
                        data-testid={`collections-remind-${r.bed_id}`}
                      >
                        <BellRing className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-lg text-emerald-600"
                        disabled={!r.pay_token || !waPhone(r.tenant_phone)}
                        onClick={() => whatsapp(r)}
                        title={waPhone(r.tenant_phone) ? "Send the payment link on WhatsApp" : "Needs a valid 10-digit mobile number"}
                        data-testid={`collections-whatsapp-${r.bed_id}`}
                      >
                        <MessageCircle className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="rounded-lg"
                        disabled={!r.pay_token}
                        onClick={() => copyLink(r)}
                        title="Copy the tenant's payment link"
                        data-testid={`collections-copy-link-${r.bed_id}`}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
